import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role } from "../lib/permissions";
import { api } from "./client";
import { wk } from "./finance";
import { qk } from "./session";
import type { Agent, ApiKey, AuditEntry, Invitation, KeyScope, Member, Workspace } from "./types";

const base = (wid: string) => `/workspaces/${wid}`;
const adminKeys = {
  apiKeys: ["api-keys"] as const,
  agents: ["agents"] as const,
};

// ---------- workspaces ----------
export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string }) =>
      (await api.post<{ workspace: Workspace }>("/workspaces", body)).workspace,
    onSuccess: (created) => {
      qc.setQueryData<Workspace[]>(qk.workspaces, (list) => (list ? [...list, created] : [created]));
      void qc.invalidateQueries({ queryKey: qk.workspaces });
    },
  });
}

export function useUpdateWorkspace(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; defaultCurrency: string; timezone: string }) =>
      api.patch(base(wid), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workspaces }),
  });
}

/**
 * Tira o workspace do cache antes de sair dele: assim `/` não redireciona de volta para ele
 * enquanto a lista é recarregada.
 */
export function useForgetWorkspace() {
  const qc = useQueryClient();
  return (wid: string) => {
    qc.setQueryData<Workspace[]>(qk.workspaces, (list) => list?.filter((w) => w.id !== wid));
    qc.removeQueries({ queryKey: ["w", wid] });
    void qc.invalidateQueries({ queryKey: qk.workspaces });
  };
}

export function useDeleteWorkspace(wid: string) {
  return useMutation({ mutationFn: () => api.delete(base(wid)) });
}

export function useLeaveWorkspace(wid: string) {
  return useMutation({ mutationFn: () => api.post(`${base(wid)}/leave`) });
}

export function useTransferOwnership(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post(`${base(wid)}/transfer-ownership`, { userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.workspaces });
      void qc.invalidateQueries({ queryKey: wk(wid, "members") });
    },
  });
}

// ---------- membros e convites ----------
export function useMembers(wid: string) {
  return useQuery({
    queryKey: wk(wid, "members"),
    queryFn: async () => (await api.get<{ members: Member[] }>(`${base(wid)}/members`)).members,
  });
}

function useMembersMutation<I>(wid: string, fn: (input: I) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: wk(wid, "members") }),
  });
}

export function useChangeRole(wid: string) {
  return useMembersMutation(wid, ({ userId, role }: { userId: string; role: Role }) =>
    api.patch(`${base(wid)}/members/${userId}`, { role }),
  );
}

export function useRemoveMember(wid: string) {
  return useMembersMutation(wid, (userId: string) => api.delete(`${base(wid)}/members/${userId}`));
}

export function useInvitations(wid: string, enabled: boolean) {
  return useQuery({
    queryKey: wk(wid, "invitations"),
    queryFn: async () =>
      (await api.get<{ invitations: Invitation[] }>(`${base(wid)}/invitations`)).invitations,
    enabled,
  });
}

function useInvitationsMutation<I>(wid: string, fn: (input: I) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: wk(wid, "invitations") }),
  });
}

export function useInvite(wid: string) {
  return useInvitationsMutation(wid, (body: { email: string; role: Role }) =>
    api.post(`${base(wid)}/invitations`, body),
  );
}

export function useRevokeInvitation(wid: string) {
  return useInvitationsMutation(wid, (id: string) => api.delete(`${base(wid)}/invitations/${id}`));
}

// ---------- tokens de API e agentes (do usuário; a tela filtra pelo workspace) ----------
export function useApiKeys() {
  return useQuery({
    queryKey: adminKeys.apiKeys,
    queryFn: async () => (await api.get<{ apiKeys: ApiKey[] }>("/api-keys")).apiKeys,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; workspaceId: string; scope: KeyScope }) =>
      api.post<{ apiKey: ApiKey; token: string }>("/api-keys", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.apiKeys }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.apiKeys }),
  });
}

export function useAgents() {
  return useQuery({
    queryKey: adminKeys.agents,
    queryFn: async () => (await api.get<{ agents: Agent[] }>("/agents")).agents,
  });
}

export function useRevokeAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/agents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.agents }),
  });
}

// ---------- preferências e auditoria ----------
export function useEmailPreferences(wid: string) {
  return useQuery({
    queryKey: wk(wid, "email-preferences"),
    queryFn: () => api.get<{ businessEmails: boolean }>(`${base(wid)}/email-preferences`),
  });
}

export function useSetEmailPreferences(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (businessEmails: boolean) =>
      api.put<{ businessEmails: boolean }>(`${base(wid)}/email-preferences`, { businessEmails }),
    onSuccess: (data) => qc.setQueryData(wk(wid, "email-preferences"), data),
  });
}

export function useAudit(wid: string) {
  return useInfiniteQuery({
    queryKey: wk(wid, "audit"),
    queryFn: ({ pageParam }) =>
      api.get<{ entries: AuditEntry[]; nextCursor: string | null }>(
        `${base(wid)}/audit?limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
